import { MessageRole } from '@/constants';
import type { ChatMessage } from '@/types/chat';

/** A prompt that failed in this session and has no id from the BE, so `GET /chat` may not list it. */
export const isUnsavedFailedPrompt = (message: ChatMessage) => message.status === 'FAILED' && !message.messageId;

/**
 * Puts this session's failed prompts back into freshly loaded messages, each after the message it
 * followed. A loaded prompt with the same text past that point is the BE's copy. It wins once the BE
 * has recorded how the run ended; while the BE still has it as `SENT`, the failed copy stays under the
 * BE's id, so later hydrations line it up with that copy.
 */
export const keepUnsavedFailedPrompts = (cached: ChatMessage[], loaded: ChatMessage[]): ChatMessage[] => {
  if (!cached.some(isUnsavedFailedPrompt)) return loaded;

  const merged = [...loaded];
  let insertAt = 0;

  cached.forEach((message) => {
    if (!isUnsavedFailedPrompt(message)) {
      const loadedIndex = message.messageId ? merged.findIndex((m) => m.messageId === message.messageId) : -1;
      if (loadedIndex !== -1) insertAt = loadedIndex + 1;
      return;
    }

    const savedIndex = merged.findIndex(
      (m, index) => index >= insertAt && m.role === MessageRole.User && m.content === message.content,
    );
    if (savedIndex === -1) {
      merged.splice(insertAt, 0, message);
      insertAt += 1;
      return;
    }

    const saved = merged[savedIndex];
    if (saved.status === 'SENT') {
      merged[savedIndex] = { ...message, messageId: saved.messageId };
    } else if (message.attachedDocuments?.length && !saved.attachedDocuments?.length) {
      merged[savedIndex] = { ...saved, attachedDocuments: message.attachedDocuments };
    }
    insertAt = savedIndex + 1;
  });

  return merged;
};
